import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga, Reproductor } from '../curso/componentes';
import { fechaDia } from '../temario/queries';

type Recordings = components['schemas']['Recordings'];
type Player = components['schemas']['RecordingPlayer'];

/** La vista se registra a los 3 minutos de abrir el video, como el PHP. */
const VISTA_MS = 180_000;

/** Clases grabadas (port de `inicio/live/historico3`). */
export function GrabacionesPage() {
  return (
    <PermissionGate permiso="live.history.access" modulo="Clases grabadas">
      <Grabaciones />
    </PermissionGate>
  );
}

function Grabaciones() {
  const { data, error, isPending } = useQuery({
    queryKey: ['grabaciones'],
    queryFn: () => unwrap<Recordings>(api.GET('/api/v1/live/recordings')),
  });
  const [params, setParams] = useSearchParams();
  const [video, setVideo] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const grupoId = Number(params.get('licencia')) || data?.groups[0]?.membershipId;
  const grupo = data?.groups.find((g) => g.membershipId === grupoId) ?? data?.groups[0];
  useStagger(root, '[data-card]', { step: 40, deps: [grupo?.membershipId] });

  if (isPending) return <Cargando alto="h-80" />;
  if (error) return <ErrorCarga error={error} />;

  return (
    <div className="space-y-6">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Clases grabadas</span>
      </h1>
      {/* Aviso + banner «actitud» de `historico3`. */}
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="grid place-items-center rounded-[20px] border border-cian-300/60 bg-cian-300/15 px-5 py-6 text-center text-sm text-azul-texto uppercase">
          <p>
            Recuerda que puedes ver estas clases las veces que quieras.
            {data.deadline && (
              <strong className="mt-2 block">
                Fecha límite de reproducción
                <br />
                {fechaDia(data.deadline, false)}
              </strong>
            )}
          </p>
        </div>
        <img
          src="/img/live/actitud.png"
          alt="¡La clave del éxito es cuestión de actitud y no de aptitud! Recuerda que si tienes alguna duda con gusto te ayudaremos a resolverlas a través del foro de dudas."
          className="w-full self-center rounded-[10px]"
        />
      </div>
      {data.groups.length === 0 ? (
        <p className="panel-vidrio text-center text-texto-2">
          Aún no hay grabaciones para tus licencias.
        </p>
      ) : (
        <>
          {data.groups.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {data.groups.map((g) => (
                <button
                  key={g.membershipId}
                  type="button"
                  className="btn-modulo"
                  data-state={g.membershipId === grupo?.membershipId ? 'active' : undefined}
                  onClick={() => {
                    setParams({ licencia: String(g.membershipId) }, { replace: true });
                    setVideo(null);
                  }}
                >
                  {g.name}
                </button>
              ))}
            </div>
          )}
          {video !== null && <Video key={video} id={video} />}
          {/* Miniaturas del legacy: imagen de video + nombre y descripción. */}
          <div ref={root} className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {grupo?.videos.map((v) => (
              <button
                key={v.id}
                data-card
                type="button"
                className={cn(
                  'card-vidrio card-vidrio-hover flex flex-col overflow-hidden p-0 text-left',
                  v.id === video && 'ring-2 ring-azul-600',
                )}
                onClick={() => {
                  setVideo(v.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <img
                  src="/img/live/video.jpg"
                  alt=""
                  className="aspect-[640/350] w-full object-cover"
                />
                <span className="flex flex-1 flex-col gap-1 p-3">
                  <span className="block text-sm font-bold">{v.name}</span>
                  {v.description && (
                    <span className="line-clamp-3 text-xs text-texto-2">{v.description}</span>
                  )}
                  <span className="mt-auto pt-1 text-xs text-texto-3">
                    {v.views > 0
                      ? `Vista ${String(v.views)} ${v.views === 1 ? 'vez' : 'veces'}`
                      : 'Sin ver'}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Video({ id }: { id: number }) {
  const qc = useQueryClient();
  const { data, error, isPending } = useQuery({
    queryKey: ['grabacion', id],
    queryFn: () =>
      unwrap<Player>(
        api.GET('/api/v1/live/recordings/{id}', { params: { path: { id: String(id) } } }),
      ),
  });
  const vista = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/live/recordings/{id}/views', { params: { path: { id: String(id) } } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grabaciones'] }),
  });
  useEffect(() => {
    if (!data) return;
    const t = window.setTimeout(() => {
      vista.mutate();
    }, VISTA_MS);
    return () => {
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);
  if (isPending) return <Cargando alto="aspect-video" />;
  if (error) return <ErrorCarga error={error} />;
  return (
    <section className="grid gap-4 lg:grid-cols-[7fr_5fr]">
      <Reproductor embedUrl={data.embedUrl} titulo={data.name} />
      <div className="panel-vidrio self-start">
        <h2 className="text-lg font-extrabold text-azul-texto">{data.name}</h2>
        <div className="mt-1 mb-2 h-[3px] w-16 rounded-full bg-verde-500" />
        {data.description && <p className="text-sm text-texto-2">{data.description}</p>}
      </div>
    </section>
  );
}
